"use client";

import React from "react";
import { useGame } from "@/app/providers/GameContextProvider";
import { useTranslation } from "@/hooks/useTranslation";
import { EXPANSION_CONFIG, MAP_TYPE_CONFIG } from "@/lib/utils/gameConfigUtils";
import Image from "next/image";
import ButtonFFXIV from "@/app/components/ButtonFFXIV";
import PanelFFXIV from "@/app/components/PanelFFXIV";
import TextFFXIV from "@/app/components/TextFFXIV";
import VerticalGoldenSeparator from "@/app/components/VerticalGoldenSeparator";
import GameModeConfigSeparator from "../GameModes/GameModeConfigSeparator";
import { FaRegCopy } from "react-icons/fa";
import TooltipFFXIV from "@/app/components/TooltipFFXIV";
import { useGlobalPopup } from "@/app/providers/GlobalPopupProvider";
import { GameMode } from "@/lib/types/common/GameMode";
import { CustomModeSettings } from "@/lib/types/common/GameModeAdvancedSettings";
import TooltipWithIconFFXIV from "@/app/components/TooltipWithIconFFXIV";
import { useAudio, AUDIO } from '@/app/providers/AudioProvider';
import { getDailyNumber } from "@/lib/utils/dailyUtils";


type SettingType = 'rounds' | 'players' | 'timePerRound' | 'flashDuration' | 'hazardsPerRound' | 'teamMaxHp' | 'damageScaling';

const GAME_MODE_SETTINGS: Partial<Record<GameMode, SettingType[]>> = {
    [GameMode.CLASSIC]: ['rounds', 'players'],
    [GameMode.TIMED]: ['rounds', 'players', 'timePerRound'],
    [GameMode.ENDLESS]: ['players'],
    [GameMode.FLASH]: ['rounds', 'players', 'flashDuration'],
    [GameMode.HAZARD]: ['rounds', 'players', 'timePerRound', 'hazardsPerRound'],
    [GameMode.DAILY]: ['rounds', 'timePerRound'],
    [GameMode.FIGHT]: ['players', 'timePerRound', 'teamMaxHp', 'damageScaling'],
    [GameMode.COOP]: ['rounds', 'players', 'timePerRound'],
};

// Get settings to display for Custom mode based on its configuration
const getCustomModeSettings = (gameSettings: CustomModeSettings): SettingType[] => {
    const settings: SettingType[] = ['players'];
    const baseMode = gameSettings?.baseMode;

    // Show rounds unless endless base mode (which has no rounds)
    if (baseMode !== GameMode.ENDLESS && baseMode !== GameMode.FIGHT) {
        settings.push('rounds');
    }

    // Show timer if there is one
    if (gameSettings?.timerSeconds && gameSettings.timerSeconds > 0) {
        settings.push('timePerRound');
    }

    // Show flash duration if flash mode is enabled
    if (gameSettings?.hasFlashMode && gameSettings?.flashDuration) {
        settings.push('flashDuration');
    }

    // Show hazards per round if hazards are enabled
    if (gameSettings?.hasHazards && gameSettings?.hazardsPerRound) {
        settings.push('hazardsPerRound');
    }

    // Show fight/coop specific settings when applicable
    if (baseMode === GameMode.FIGHT || baseMode === GameMode.COOP) {
        if (gameSettings?.teamMaxHp) {
            settings.push('teamMaxHp');
        }
        if (gameSettings?.damageMultiplierScaling) {
            settings.push('damageScaling');
        }
    }

    return settings;
};

interface GameSettingsProps {
    isMobile?: boolean;
}

const GameSettings = ({ isMobile = false }: GameSettingsProps) => {
    const { gameState, room } = useGame();
    const { showPopup } = useGlobalPopup();
    const { t } = useTranslation();
    const { play } = useAudio();

    if (!gameState || !room) return null;

    const handleBackToLobby = () => {
        room.send("backToLobby");
    };

    const handleCopyDailyRecap = () => {
        if (gameState.gameMode !== GameMode.DAILY) return;

        // Get the daily number
        const dailyNumber = getDailyNumber();
        
        // Get player's final ranking (should be only one player in daily mode)
        const playerRanking = gameState.finalRankings && gameState.finalRankings.length > 0
            ? gameState.finalRankings[0]
            : null;
        
        if (!playerRanking) return;

        // Build the recap text
        let recapText = t('play.dailyRecapHeader', { score: playerRanking.totalScore, dailyNumber }) + '\n';
        
        // Add each round score
        gameState.roundHistory.forEach((round) => {
            const roundNum = round.roundNumber;
            const playerResult = round.playerResults.get(playerRanking.sessionId);
            if (playerResult && playerResult.scores && playerResult.scores.length >= roundNum) {
                const roundScore = playerResult.scores[roundNum - 1];
                recapText += t('play.dailyRecapRound', { roundNum, roundScore }) + '\n';
            }
        });
        
        recapText += `https://eorguessr.com`;

        // Copy to clipboard
        navigator.clipboard.writeText(recapText).then(() => {
            play(AUDIO.CLICK);
            showPopup({
                type: 'success',
                message: t('play.copyRecapSuccess') || 'Recap copied to clipboard!',
            });
        }).catch((err) => {
            console.error('Failed to copy recap:', err);
            showPopup({
                type: 'error',
                message: t('play.copyRecapError') || 'Failed to copy recap',
            });
        });
    };

    // Get settings to display based on game mode
    const settingsToShow = gameState.gameSettingsSnapshot.gameMode === GameMode.CUSTOM
        ? getCustomModeSettings(gameState.gameSettingsSnapshot as unknown as CustomModeSettings)
        : GAME_MODE_SETTINGS[gameState.gameSettingsSnapshot.gameMode as GameMode] || [];

    return (
        <PanelFFXIV className={`border-2 border-x-[#c0a270] border-y-[#e0c290] flex flex-col gap-3 !pt-2 !pb-2.5 ${isMobile ? '!px-3 w-full max-w-[95vw]' : '!px-5 w-[650px]'}`}>
            <div className="w-full text-center">
                <TextFFXIV className="text-lg font-medium" text={t("play.recap.gameSettings")} />
                <GameModeConfigSeparator />
            </div>

            <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} gap-5`}>
                <div className={`flex flex-col ${isMobile ? 'w-full' : 'w-[250px]'}`}>
                    {(() => {
                        // Use snapshot if available (preserves settings even when lobby changes them)
                        const snapshot = gameState.gameSettingsSnapshot;
                        const hasSnapshot = snapshot && snapshot.gameMode;
                        
                        // Get values from snapshot or fall back to live state
                        const displayGameMode = hasSnapshot ? snapshot.gameMode : gameState.gameMode;
                        const displayBaseMode = hasSnapshot ? snapshot.baseMode : gameState.gameSettings?.baseMode;
                        const displayMaxRounds = hasSnapshot ? snapshot.maxRounds : (gameState.gameSettings?.maxRounds ?? gameState.maxRounds);
                        const displayPlayerCount = hasSnapshot ? snapshot.playerCount : (
                            gameState.finalRankings && gameState.finalRankings.length > 0
                                ? gameState.finalRankings.length
                                : Array.from(gameState.players?.entries() || []).length
                        );
                        const displayTimerSeconds = hasSnapshot ? snapshot.timerSeconds : (gameState.gameSettings?.timerSeconds ?? 60);
                        const displayFlashDuration = hasSnapshot ? snapshot.flashDuration : (gameState.gameSettings?.flashDuration ?? 1000);
                        const displayHazardsPerRound = hasSnapshot ? snapshot.hazardsPerRound : (gameState.gameSettings?.hazardsPerRound ?? 1);
                        const displayTeamMaxHp = hasSnapshot ? snapshot.teamMaxHp : (gameState.gameSettings?.teamMaxHp ?? 10000);
                        const displayDamageScaling = hasSnapshot ? snapshot.damageMultiplierScaling : (gameState.gameSettings?.damageMultiplierScaling ?? 'normal');

                        return (
                            <div className="flex flex-col gap-2">
                                <div className="flex justify-between">
                                    <TextFFXIV text={t("play.recap.gameMode")} />
                                    <TextFFXIV text={t(`game.modes.${displayGameMode}.name`)} />
                                </div>
                                {displayGameMode === GameMode.CUSTOM && displayBaseMode && (
                                    <div className="flex justify-between">
                                        <TextFFXIV text={t("play.recap.baseMode")} />
                                        <TextFFXIV text={t(`game.modes.${displayBaseMode}.name`)} />
                                    </div>
                                )}
                                {settingsToShow.includes('rounds') && (
                                    <div className="flex justify-between">
                                        <TextFFXIV text={t("play.recap.rounds")} />
                                        <TextFFXIV text={displayMaxRounds.toString()} />
                                    </div>
                                )}
                                {settingsToShow.includes('players') && (
                                    <div className="flex justify-between">
                                        <TextFFXIV text={t("play.recap.playerCount")} />
                                        <TextFFXIV text={displayPlayerCount.toString()} />
                                    </div>
                                )}
                                {settingsToShow.includes('timePerRound') && (
                                    <div className="flex justify-between">
                                        <TextFFXIV text={t("play.recap.timePerRound")} />
                                        <TextFFXIV text={`${displayTimerSeconds}s`} />
                                    </div>
                                )}
                                {settingsToShow.includes('flashDuration') && (
                                    <div className="flex justify-between">
                                        <TextFFXIV text={t("play.recap.flashDuration")} />
                                        <TextFFXIV text={`${displayFlashDuration}ms`} />
                                    </div>
                                )}
                                {settingsToShow.includes('hazardsPerRound') && (
                                    <div className="flex justify-between">
                                        <TextFFXIV text={t("play.recap.hazardsPerRound")} />
                                        <TextFFXIV text={displayHazardsPerRound.toString()} />
                                    </div>
                                )}
                                {settingsToShow.includes('teamMaxHp') && (
                                    <div className="flex justify-between">
                                        <TextFFXIV text={t("play.recap.teamMaxHp")} />
                                        <TextFFXIV text={displayTeamMaxHp.toString() + " HP"} />
                                    </div>
                                )}
                                {settingsToShow.includes('damageScaling') && (
                                    <div className="flex justify-between">
                                        <TextFFXIV text={t("play.recap.damageScaling")} />
                                        <TextFFXIV text={t(`game.config.advancedSettings.damageScaling${displayDamageScaling.charAt(0).toUpperCase() + displayDamageScaling.slice(1)}`)} />
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </div>
                {!isMobile && (
                    <VerticalGoldenSeparator
                        heightOverflow={1}
                        className="-translate-y-0"
                    />
                )}
                {isMobile && <GameModeConfigSeparator />}
                <div className="flex flex-col items-center">
                    {(() => {
                        // Use snapshot if available for expansions/map types
                        const snapshot = gameState.gameSettingsSnapshot;
                        const hasSnapshot = snapshot && snapshot.selectedExpansions && snapshot.selectedExpansions.length > 0;
                        const displayExpansions = hasSnapshot ? Array.from(snapshot.selectedExpansions) : Array.from(gameState.selectedExpansions);
                        const displayMapTypes = hasSnapshot ? Array.from(snapshot.selectedMapTypes) : Array.from(gameState.selectedMapTypes);
                        
                        return (
                            <>
                                <TextFFXIV text={t("play.expansions")} />
                                <div className={`grid grid-cols-6 gap-2 mt-1 mb-4`}>
                                    {EXPANSION_CONFIG.map((config) => {
                                        const isSelected = displayExpansions.includes(config.expansion);
                                        return (
                                            <div key={config.expansion} className="group relative">
                                                <div className={`${!isSelected ? 'opacity-30' : ''} relative w-8 h-8`}>
                                                    {isSelected && (
                                                        <div className="left-[-2px] top-[-2px] rounded-md absolute w-[36px] h-[36px] z-40 border-2 border-[#CDCCA8] shadow-[inset_0px_0px_2px_2px_rgba(0,0,0,0.6),0px_0px_2px_2px_rgba(0,0,0,0.5)]"></div>
                                                    )}
                                                    <Image
                                                        src={config.icon}
                                                        alt={t(config.name)}
                                                        width={32}
                                                        height={32}
                                                        className="w-8 h-8 shadow shadow-black/50 rounded"
                                                    />
                                                </div>
                                                <TooltipFFXIV><span className="text-white">{t(config.name)}</span></TooltipFFXIV>
                                            </div>
                                        );
                                    })}
                                </div>
                                <TextFFXIV text={t("play.mapSets")} />
                                <div className={`grid grid-cols-6 gap-2 mt-1`}>
                                    {MAP_TYPE_CONFIG.map((config) => {
                                        const isSelected = displayMapTypes.includes(config.mapType);
                                        return (
                                            <div key={config.mapType} className="group relative">
                                                <div className={`${!isSelected ? 'opacity-30' : ''} relative w-8 h-8 rounded shadow shadow-black/50`}>
                                                    {isSelected && (
                                                        <div className="left-[-2px] top-[-2px] rounded-md absolute w-[36px] h-[36px] z-40 border-2 border-[#CDCCA8] shadow-[inset_0px_0px_2px_2px_rgba(0,0,0,0.6),0px_0px_2px_2px_rgba(0,0,0,0.5)]"></div>
                                                    )}
                                                    {!isSelected && (
                                                        <>
                                                            <div className="top-[-0px] h-1 w-full bg-gradient-to-b from-white/30 to-transparent absolute"></div>
                                                            <div className="bottom-[-0px] h-[6px] w-full bg-gradient-to-t from-black/30 via-black/30 via-50% to-transparent absolute"></div>
                                                        </>
                                                    )}
                                                    <Image
                                                        src={config.icon}
                                                        alt={t(config.name)}
                                                        width={32}
                                                        height={32}
                                                    />
                                                </div>
                                                <TooltipFFXIV><span className="text-white">{t(`game.config.mapTypes.${config.mapType}`)}</span></TooltipFFXIV>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        );
                    })()}
                </div>
                <div className="flex flex-col items-center">

                </div>

            </div>
            <GameModeConfigSeparator />
            <div className={`flex ${isMobile ? 'flex-col gap-4' : 'flex-row gap-20'} justify-center items-center`}>
                {gameState.gameMode !== GameMode.DAILY && (
                    <div className="flex gap-2 items-center">
                        <TextFFXIV text={t("play.seed")} />
                        <div className="bg-[#4C4C4C] text-sm py-0.5 rounded px-3 shadow-inner shadow-black/50 flex items-center">
                            <div className="translate-y-[2px]">{gameState.gameSettings?.gameSeed || "—"}</div>
                        </div>
                        {gameState.gameSettings?.gameSeed && (
                            <>
                                <div
                                    className="ffxiv-button text-center relative group rounded-full shadow-md shadow-black/30 cursor-pointer h-6 w-6"
                                    onMouseEnter={() => play(AUDIO.HOVER)}
                                    onClick={() => {
                                        play(AUDIO.CLICK);
                                        // Copy URL with seed parameter
                                        const url = new URL(window.location.origin);
                                        url.searchParams.set('seed', gameState.gameSettings!.gameSeed);
                                        navigator.clipboard.writeText(url.toString());
                                        showPopup({
                                            type: 'success',
                                            message: t('play.copySeedSuccess'),
                                        });
                                    }}
                                >
                                    <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 rounded-full pointer-events-none"></div>
                                    <FaRegCopy className="text-md absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
                                    <TooltipFFXIV showInMobile={false}><span className="text-white">{t("play.copySeed")}</span></TooltipFFXIV>
                                </div>
                                <TooltipWithIconFFXIV text={t("play.seedTooltip")} />
                            </>
                        )}
                    </div>
                )}
                {gameState.gameMode === GameMode.DAILY && (
                    <ButtonFFXIV
                        text={t("play.copyRecap") || "Copy recap"}
                        onClick={handleCopyDailyRecap}
                        fitContent={true}
                    />
                )}
                <ButtonFFXIV
                    text={t("play.backToLobby")}
                    onClick={handleBackToLobby}
                    fitContent={true}
                />
            </div>
        </PanelFFXIV>
    );
};

export default GameSettings;